import type {
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
  PeopleTravelOption,
  PeopleTravelTrip,
} from '../types';
import {
  LOCATION_ORIGIN_X,
  LOCATION_ORIGIN_Y,
  LOCATION_X_GAP,
  defaultEventPosition,
  inferOriginLabel,
} from './peopleTravelCanvasGraph';

export function matchLocationId(
  locations: PeopleTravelLocation[],
  label: string | undefined,
): string | undefined {
  const needle = String(label || '').trim().toLowerCase();
  if (!needle) return undefined;
  const exact = locations.find(l => l.label.trim().toLowerCase() === needle);
  if (exact) return exact.id;
  return locations.find(l => {
    const loc = l.label.trim().toLowerCase();
    return loc.includes(needle) || needle.includes(loc);
  })?.id;
}

export function buildTripCanvasSeed(
  trip: PeopleTravelTrip,
  options: PeopleTravelOption[],
): {
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
} {
  const selected = options.filter(o => o.isSelected);
  const flights = selected.filter(o => o.kind === 'flight');
  const cars = selected.filter(o => o.kind === 'rental_car');
  const hotels = selected.filter(o => o.kind === 'hotel');
  const destinations = trip.destinations.filter(d => d.label.trim());

  const locations: PeopleTravelLocation[] = [];
  const needsOrigin = flights.length > 0 || cars.length > 0;
  let originId: string | undefined;

  if (needsOrigin) {
    originId = 'seed-home';
    locations.push({
      id: originId,
      label: inferOriginLabel(flights),
      sortOrder: 0,
      x: LOCATION_ORIGIN_X,
      y: LOCATION_ORIGIN_Y,
    });
  }

  destinations.forEach((dest, index) => {
    locations.push({
      id: `seed-loc-${index}`,
      label: dest.label.trim(),
      placeId: dest.placeId,
      lat: dest.lat,
      lng: dest.lng,
      sortOrder: locations.length,
      x: LOCATION_ORIGIN_X + locations.length * LOCATION_X_GAP,
      y: LOCATION_ORIGIN_Y,
      startDate: trip.startDate || undefined,
      endDate: trip.endDate || undefined,
    });
  });

  if (locations.length === 0) {
    return { locations: [], events: [], legs: [] };
  }

  const destLocations = originId ? locations.filter(l => l.id !== originId) : locations;
  const firstDest = destLocations[0];

  const events: PeopleTravelEvent[] = hotels.map((hotel, index) => {
    const pos = defaultEventPosition(index);
    const hotelPlace =
      typeof hotel.details?.city === 'string'
        ? hotel.details.city
        : typeof hotel.details?.location === 'string'
          ? hotel.details.location
          : hotel.label;
    return {
      id: `seed-event-hotel-${hotel.id}`,
      locationId:
        matchLocationId(destLocations, hotelPlace) ||
        firstDest?.id ||
        locations[0].id,
      title: hotel.label || hotel.vendor || 'Hotel',
      kind: 'hotel',
      notes: hotel.vendor,
      x: pos.x,
      y: pos.y,
    };
  });

  const legs: PeopleTravelLeg[] = [];
  const destId = firstDest?.id;

  if (originId && destId) {
    for (const flight of flights) {
      legs.push({
        id: `seed-leg-flight-${flight.id}`,
        fromLocationId: originId,
        toLocationId: destId,
        mode: 'flight',
        selectedOptionIds: [flight.id],
      });
    }
    for (const car of cars) {
      legs.push({
        id: `seed-leg-car-${car.id}`,
        fromLocationId: originId,
        toLocationId: destId,
        mode: 'rental_car',
        selectedOptionIds: [car.id],
      });
    }
  }

  return { locations, events, legs };
}
